import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { ArrowLeft, User, Layers3, Menu, Save, Bot, Building2, Globe, Check, Plus, Wrench, X } from "lucide-react";
import Logo from "@/components/Logo";
import { NotificationBell } from "@/components/NotificationBell";
import { SupplierPriceCatalog } from "@/components/SupplierPriceCatalog";
import { GenericPriceCatalog } from "@/components/GenericPriceCatalog";
import { MaterialContactsManager } from "@/components/MaterialContactsManager";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/hooks/useAuth";
import { generateTitle, generateDescription } from "@/lib/seoHelpers";
import { hasPlugin, isPluginOnlyService } from "@/services/pluginResolver";
import { FACADE_SERVICE_SLUG, normalizeServiceSlugs, resolveSlug } from "@/config/serviceCatalog";

// WORK_TYPES removed — now derived from supplier.enabled_services

type MainTab = "firma" | "sluzby" | "ceniky" | "chatbot" | "stranka";

interface SupplierData {
  id: string;
  company_name: string;
  contact_email: string | null;
  contact_phone: string | null;
  region: string | null;
  ico: string | null;
  description: string | null;
  bot_name: string | null;
  bot_intro: string | null;
  bot_bio: string | null;
  bot_faq: string | null;
  page_title: string | null;
  meta_description: string | null;
  og_image_url: string | null;
  business_type: string | null;
  service_area: string | null;
  public_tagline: string | null;
  enabled_services: string[];
  enabled_plugins: string[];
}


export default function SupplierSettings() {
  const { t } = useI18n();
  const { user, isReady } = useAuth();
  const navigate = useNavigate();
  const [supplier, setSupplier] = useState<SupplierData | null>(null);
  const [loading, setLoading] = useState(true);
  const [mainTab, setMainTab] = useState<MainTab>("firma");
  const [serviceNames, setServiceNames] = useState<Record<string, string>>({});
  const [allServices, setAllServices] = useState<{ slug: string; name: string }[]>([]);
  const [customServiceInput, setCustomServiceInput] = useState("");

  // Company form
  const [form, setForm] = useState({
    company_name: "",
    contact_email: "",
    contact_phone: "",
    ico: "",
    description: "",
    region: "",
  });
  const [savingCompany, setSavingCompany] = useState(false);

  // Bot settings
  const [botName, setBotName] = useState("");
  const [botIntro, setBotIntro] = useState("");
  const [botBio, setBotBio] = useState("");
  const [botFaq, setBotFaq] = useState("");
  const [savingBot, setSavingBot] = useState(false);

  // SEO / public page settings
  const [seoForm, setSeoForm] = useState({
    page_title: "",
    meta_description: "",
    public_tagline: "",
    business_type: "",
    service_area: "",
    og_image_url: "",
  });
  const [savingSeo, setSavingSeo] = useState(false);

  useEffect(() => {
    if (!isReady) return;
    if (!user) { navigate("/supplier/register"); return; }

    (async () => {
      const { data, error } = await supabase
        .from("suppliers")
        .select("id, company_name, contact_email, contact_phone, region, ico, description, bot_name, bot_intro, bot_bio, bot_faq, page_title, meta_description, og_image_url, business_type, service_area, public_tagline, enabled_services, enabled_plugins")
        .eq("user_id", user.id)
        .maybeSingle();

      if (error) { console.error("SupplierSettings query error:", error); }
      if (!data) { navigate("/supplier/register"); return; }
      const s = data as SupplierData;
      s.enabled_services = normalizeServiceSlugs(s.enabled_services || []);
      s.enabled_plugins = normalizeServiceSlugs(s.enabled_plugins || []);
      setSupplier(s);

      // Load service names for generic price catalog labels
      const [{ data: svcData }, { data: descData }] = await Promise.all([
        supabase.from("services").select("slug, name").eq("status", "active").order("name"),
        supabase.from("supplier_service_descriptions" as any).select("service_slug, service_name").eq("supplier_id", (data as SupplierData).id),
      ]);
      const map: Record<string, string> = {};
      const svcs: { slug: string; name: string }[] = [];
      const catalogSlugs = new Set<string>();
      if (svcData) {
        svcData.forEach((svc: { slug: string; name: string }) => {
          map[svc.slug] = svc.name;
          catalogSlugs.add(svc.slug);
          if (!isPluginOnlyService(svc.slug)) svcs.push(svc);
        });
      }
      // Custom service names from supplier_service_descriptions
      if (descData) {
        (descData as any[]).forEach((d: any) => {
          if (d.service_name && !catalogSlugs.has(d.service_slug)) {
            map[d.service_slug] = d.service_name;
          }
        });
      }
      setServiceNames(map);
      setAllServices(svcs);
      setForm({
        company_name: s.company_name || "",
        contact_email: s.contact_email || "",
        contact_phone: s.contact_phone || "",
        ico: s.ico || "",
        description: s.description || "",
        region: s.region || "",
      });
      setBotName(s.bot_name || "");
      setBotIntro(s.bot_intro || "");
      setBotBio(s.bot_bio || "");
      setBotFaq(s.bot_faq || "");
      setSeoForm({
        page_title: s.page_title || "",
        meta_description: s.meta_description || "",
        public_tagline: s.public_tagline || "",
        business_type: s.business_type || "",
        service_area: s.service_area || "",
        og_image_url: s.og_image_url || "",
      });
      setLoading(false);
    })();
  }, [isReady, user]);

  const saveCompany = async () => {
    if (!supplier || !form.company_name.trim()) return;
    setSavingCompany(true);
    const { error } = await supabase
      .from("suppliers")
      .update({
        company_name: form.company_name,
        contact_email: form.contact_email || null,
        contact_phone: form.contact_phone || null,
        ico: form.ico || null,
        description: form.description || null,
        region: form.region || null,
      })
      .eq("id", supplier.id);

    if (error) {
      toast.error(t("supplierSettings.saveError"));
    } else {
      toast.success(t("supplierSettings.savedCompany"));
      setSupplier({ ...supplier, ...form });
    }
    setSavingCompany(false);
  };

  const saveBotSettings = async () => {
    if (!supplier) return;
    setSavingBot(true);
    const { error } = await supabase
      .from("suppliers")
      .update({
        bot_name: botName || null,
        bot_intro: botIntro || null,
        bot_bio: botBio || null,
        bot_faq: botFaq || null,
      } as Record<string, unknown>)
      .eq("id", supplier.id);
    setSavingBot(false);
    if (error) {
      toast.error(t("supplierSettings.saveBotFailed"));
    } else {
      toast.success(t("supplierSettings.savedBot"));
      setSupplier({ ...supplier, bot_name: botName || null, bot_intro: botIntro || null, bot_bio: botBio || null, bot_faq: botFaq || null });
    }
  };

  const saveSeoSettings = async () => {
    if (!supplier) return;
    setSavingSeo(true);
    const { error } = await supabase
      .from("suppliers")
      .update({
        page_title: seoForm.page_title || null,
        meta_description: seoForm.meta_description || null,
        public_tagline: seoForm.public_tagline || null,
        business_type: seoForm.business_type || null,
        service_area: seoForm.service_area || null,
        og_image_url: seoForm.og_image_url || null,
      } as Record<string, unknown>)
      .eq("id", supplier.id);
    setSavingSeo(false);
    if (error) {
      toast.error(t("supplierSettings.saveError"));
    } else {
      toast.success(t("supplierSettings.savedPublicPage"));
      setSupplier({ ...supplier, ...seoForm });
    }
  };

  const toggleService = async (slug: string) => {
    if (!supplier) return;
    const normalizedSlug = resolveSlug(slug);
    const current = normalizeServiceSlugs(supplier.enabled_services || []);
    const updated = current.includes(normalizedSlug)
      ? current.filter(s => s !== normalizedSlug)
      : [...current, normalizedSlug];
    const { error } = await supabase
      .from("suppliers")
      .update({ enabled_services: updated } as any)
      .eq("id", supplier.id);
    if (error) {
      toast.error(t("supplierSettings.saveFailed"));
    } else {
      setSupplier({ ...supplier, enabled_services: updated });
    }
  };

  const slugify = (text: string) =>
    text.toLowerCase()
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

  const addCustomService = async () => {
    const name = customServiceInput.trim();
    if (!name || !supplier) return;
    const slug = slugify(name);
    if (!slug) return;
    if (normalizeServiceSlugs(supplier.enabled_services || []).includes(slug)) {
      toast.error(t("supplierSettings.serviceAlreadyAdded"));
      return;
    }
    // 1. Add slug to enabled_services
    const updated = [...(supplier.enabled_services || []), slug];
    const { error } = await supabase
      .from("suppliers")
      .update({ enabled_services: updated } as any)
      .eq("id", supplier.id);
    if (error) { toast.error(t("supplierSettings.saveFailed")); return; }
    // 2. Store display name in supplier_service_descriptions
    await supabase
      .from("supplier_service_descriptions" as any)
      .upsert({
        supplier_id: supplier.id,
        service_slug: slug,
        service_name: name,
        description: "",
      } as any, { onConflict: "supplier_id,service_slug" });
    // 3. Update local state
    setServiceNames(prev => ({ ...prev, [slug]: name }));
    setSupplier({ ...supplier, enabled_services: updated });
    setCustomServiceInput("");
    toast.success(t("supplierSettings.serviceAdded").replace("{name}", name));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">...</div>
      </div>
    );
  }

  if (!supplier) return null;

  // Generate preview for SEO tab
  const previewTitle = seoForm.page_title || generateTitle({ ...supplier, ...seoForm, page_title: null } as any);
  const previewDesc = seoForm.meta_description || generateDescription({ ...supplier, ...seoForm, meta_description: null } as any);

  // Plugin resolution: show facade plugin UI only when both service + plugin are enabled
  const hasFasady = hasPlugin(FACADE_SERVICE_SLUG, supplier?.enabled_services || [], supplier?.enabled_plugins || []);

  // Services that have generic pricing — exclude plugin-only services entirely
  // (facade service never shows in generic catalog, it's plugin-only)
  const genericServices = (supplier?.enabled_services || []).filter(slug =>
    !isPluginOnlyService(slug)
  );
  const hasAnyServices = (supplier?.enabled_services || []).length > 0;

  // Custom services = slugs in enabled_services that are NOT in the global catalog
  const catalogSlugs = new Set(allServices.map(s => s.slug));
  const customServices = (supplier?.enabled_services || []).filter(slug =>
    !catalogSlugs.has(slug) && !isPluginOnlyService(slug)
  );

  const tabs: { key: MainTab; label: string; icon: React.ReactNode }[] = [
    { key: "firma", label: t("supplier.tabCompany"), icon: <Building2 className="w-4 h-4" /> },
    { key: "sluzby", label: t("supplierSettings.tabServices"), icon: <Wrench className="w-4 h-4" /> },
    { key: "chatbot", label: t("supplier.tabChatbot"), icon: <Bot className="w-4 h-4" /> },
    { key: "stranka", label: t("supplierSettings.tabPublicPage"), icon: <Globe className="w-4 h-4" /> },
  ];

  const inputCls = "w-full text-sm bg-background border border-border rounded-lg px-3 py-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/50";

  return (
    <div className="flex flex-col h-[100dvh] bg-background overflow-hidden">
      <header className="flex items-center justify-between px-4 h-12">
        <div className="flex items-center gap-0.5 sm:gap-2">
          <button
            onClick={() => navigate("/supplier/dashboard")}
            className="p-1.5 sm:p-2 rounded-lg hover:bg-accent transition-colors text-muted-foreground -ml-1 sm:ml-0"
          >
            <Menu className="w-5 h-5" />
          </button>
          <Link to="/" className="flex items-center gap-1 text-foreground text-base hover:opacity-70 transition-opacity">
            <Logo size={32} />
            <span className="font-semibold">cenik.ai</span>
          </Link>
        </div>
        <div className="flex items-center gap-1.5 sm:gap-2">
          <Link to="/profile" className="relative p-2 rounded-full hover:bg-accent transition-colors text-muted-foreground">
            <User className="w-5 h-5" />
          </Link>
          <NotificationBell />
        </div>
      </header>

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
          <div className="mb-4 flex items-center gap-2">
            <button onClick={() => navigate("/supplier/dashboard")} className="p-2 rounded-full hover:bg-accent transition-colors">
              <ArrowLeft className="w-4 h-4 text-foreground" />
            </button>
            <span className="font-semibold text-foreground text-sm">{form.company_name || supplier.company_name}</span>
            <span className="text-muted-foreground text-xs">{t("supplier.settingsTitle")}</span>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mb-6 border-b border-border overflow-x-auto scrollbar-none">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setMainTab(tab.key)}
                className={`flex items-center gap-1.5 px-3 sm:px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${
                  mainTab === tab.key
                    ? "border-primary text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>

          {/* Firma tab */}
          {mainTab === "firma" && (
            <div className="space-y-4">
              <div className="border border-border rounded-xl bg-card p-4 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">{t("supplierSettings.companyName")}</label>
                    <input type="text" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className={inputCls} />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">{t("supplierSettings.ico")}</label>
                    <input type="text" value={form.ico} onChange={(e) => setForm({ ...form, ico: e.target.value })} className={inputCls} />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">{t("supplierSettings.contactEmail")}</label>
                    <input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className={inputCls} />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">{t("supplierSettings.contactPhone")}</label>
                    <input type="tel" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} className={inputCls} />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">{t("supplierSettings.region")}</label>
                    <input type="text" value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} placeholder={t("supplierSettings.regionPlaceholder")} className={inputCls} />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">{t("supplierSettings.description")}</label>
                  <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} placeholder={t("supplierSettings.descriptionPlaceholder")} className={inputCls + " resize-none"} />
                </div>
              </div>

              {/* Material contacts — fasády plugin only */}
              {hasFasady && (
                <div className="border border-border rounded-xl bg-card p-4">
                  <MaterialContactsManager supplierId={supplier.id} />
                </div>
              )}

              <button onClick={saveCompany} disabled={savingCompany || !form.company_name.trim()} className="w-full flex items-center justify-center gap-2 py-3 bg-primary text-primary-foreground rounded-xl text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50">
                <Save className="w-4 h-4" />
                {savingCompany ? t("supplierSettings.saving") : t("supplierSettings.saveCompany")}
              </button>
            </div>
          )}

          {/* Chatbot tab */}
          {mainTab === "chatbot" && (
            <div className="space-y-5">
              <p className="text-sm text-muted-foreground">
                {t("supplierSettings.chatbotDesc")}
              </p>

              <div className="space-y-1.5">
                <label className="text-xs font-medium text-foreground">{t("supplierSettings.botName")}</label>
                <input type="text" value={botName} onChange={(e) => setBotName(e.target.value)} placeholder={`${form.company_name || supplier.company_name} asistent`} className={inputCls} />
                <p className="text-[11px] text-muted-foreground">{t("supplierSettings.botNameHint")}</p>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-medium text-foreground">{t("supplierSettings.botIntro")}</label>
                <textarea value={botIntro} onChange={(e) => setBotIntro(e.target.value)} rows={3} placeholder={t("supplierSettings.botIntroPlaceholder")} className={inputCls + " resize-none"} />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-medium text-foreground">{t("supplierSettings.aboutCompany")}</label>
                <textarea value={botBio} onChange={(e) => setBotBio(e.target.value)} rows={4} placeholder={t("supplierSettings.aboutCompanyPlaceholder")} className={inputCls + " resize-none"} />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-medium text-foreground">{t("supplierSettings.faq")}</label>
                <textarea value={botFaq} onChange={(e) => setBotFaq(e.target.value)} rows={6} placeholder={t("supplierSettings.faqPlaceholder")} className={inputCls + " resize-none"} />
              </div>

              <button onClick={saveBotSettings} disabled={savingBot} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 disabled:opacity-50 transition-opacity">
                <Save className="w-4 h-4" />
                {savingBot ? t("supplierSettings.saving") : t("supplierSettings.saveBot")}
              </button>
            </div>
          )}

          {/* Veřejná stránka tab */}
          {mainTab === "stranka" && (
            <div className="space-y-5">
              <p className="text-sm text-muted-foreground">
                {t("supplierSettings.publicPageDesc")}
              </p>

              <div className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-foreground">{t("supplierSettings.tagline")}</label>
                  <input type="text" value={seoForm.public_tagline} onChange={(e) => setSeoForm({ ...seoForm, public_tagline: e.target.value })} placeholder={t("supplierSettings.taglinePlaceholder")} className={inputCls} />
                  <p className="text-[11px] text-muted-foreground">{t("supplierSettings.taglineHint")}</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-foreground">{t("supplierSettings.businessType")}</label>
                    <input type="text" value={seoForm.business_type} onChange={(e) => setSeoForm({ ...seoForm, business_type: e.target.value })} placeholder={t("supplierSettings.businessTypePlaceholder")} className={inputCls} />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-foreground">{t("supplierSettings.serviceArea")}</label>
                    <input type="text" value={seoForm.service_area} onChange={(e) => setSeoForm({ ...seoForm, service_area: e.target.value })} placeholder={t("supplierSettings.serviceAreaPlaceholder")} className={inputCls} />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-foreground">{t("supplierSettings.seoTitle")}</label>
                  <input type="text" value={seoForm.page_title} onChange={(e) => setSeoForm({ ...seoForm, page_title: e.target.value })} placeholder={generateTitle({ ...supplier, page_title: null } as any)} className={inputCls} />
                  <p className="text-[11px] text-muted-foreground">{t("supplierSettings.seoTitleHint")}</p>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-foreground">{t("supplierSettings.seoDesc")}</label>
                  <textarea value={seoForm.meta_description} onChange={(e) => setSeoForm({ ...seoForm, meta_description: e.target.value })} rows={3} placeholder={generateDescription({ ...supplier, meta_description: null } as any)} className={inputCls + " resize-none"} />
                  <div className="flex justify-between">
                    <p className="text-[11px] text-muted-foreground">{t("supplierSettings.seoDescHint")}</p>
                    <span className={`text-[11px] ${(seoForm.meta_description || "").length > 160 ? "text-destructive" : "text-muted-foreground"}`}>
                      {(seoForm.meta_description || "").length}/160
                    </span>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-foreground">{t("supplierSettings.ogImage")}</label>
                  <input type="url" value={seoForm.og_image_url} onChange={(e) => setSeoForm({ ...seoForm, og_image_url: e.target.value })} placeholder="https://vasefirma.cz/obrazek.jpg" className={inputCls} />
                  <p className="text-[11px] text-muted-foreground">{t("supplierSettings.ogImageHint")}</p>
                </div>
              </div>

              {/* Google search preview */}
              <div className="border border-border rounded-xl p-4 bg-muted/30">
                <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide mb-2">{t("supplierSettings.searchPreview")}</p>
                <div className="space-y-0.5">
                  <p className="text-sm text-blue-700 dark:text-blue-400 font-medium truncate">{previewTitle}</p>
                  <p className="text-[11px] text-green-700 dark:text-green-500 font-mono">cenik.ai/{supplier.company_name?.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}</p>
                  <p className="text-xs text-muted-foreground line-clamp-2">{previewDesc}</p>
                </div>
              </div>

              <button onClick={saveSeoSettings} disabled={savingSeo} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 disabled:opacity-50 transition-opacity">
                <Save className="w-4 h-4" />
                {savingSeo ? t("supplierSettings.saving") : t("supplierSettings.savePublicPage")}
              </button>
            </div>
          )}

          {/* Služby & ceníky tab */}
          {mainTab === "sluzby" && supplier && (
            <div className="space-y-6">
              {/* Catalog services */}
              <div className="border border-border rounded-xl bg-card p-4 space-y-3">
                <div>
                  <h3 className="text-sm font-semibold text-foreground">{t("supplierSettings.servicesHeading")}</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">{t("supplierSettings.servicesDesc")}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {allServices.map(svc => {
                    const isActive = (supplier.enabled_services || []).includes(svc.slug);
                    return (
                      <button
                        key={svc.slug}
                        onClick={() => toggleService(svc.slug)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                          isActive
                            ? "bg-primary text-primary-foreground border-primary"
                            : "bg-background text-muted-foreground border-border hover:border-primary/50 hover:text-foreground"
                        }`}
                      >
                        {isActive && <Check className="w-3 h-3" />}
                        {svc.name}
                      </button>
                    );
                  })}
                  {/* Custom services inline */}
                  {customServices.map(slug => (
                    <span key={slug} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-primary text-primary-foreground border border-primary">
                      <Check className="w-3 h-3" />
                      {serviceNames[slug] || slug}
                      <button
                        onClick={() => toggleService(slug)}
                        className="ml-0.5 hover:opacity-70 transition-opacity"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
                {/* Add custom service inline */}
                <div className="flex gap-2 pt-1">
                  <input
                    type="text"
                    value={customServiceInput}
                    onChange={(e) => setCustomServiceInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addCustomService()}
                    placeholder={t("supplierSettings.addCustomService")}
                    className="flex-1 text-sm bg-background border border-border rounded-lg px-3 py-2 text-foreground placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-primary/20"
                  />
                  <button
                    onClick={addCustomService}
                    disabled={!customServiceInput.trim()}
                    className="flex items-center gap-1 px-3 py-2 bg-primary text-primary-foreground text-sm rounded-lg hover:opacity-90 disabled:opacity-50 transition-opacity shrink-0"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    {t("supplierSettings.addButton")}
                  </button>
                </div>
              </div>

              {/* Facade plugin pricing (custom insulation grid) */}
              {hasFasady && (
                <SupplierPriceCatalog supplierId={supplier.id} />
              )}

              {/* Generic pricing for non-plugin services */}
              {genericServices.map(slug => (
                <div key={slug} className="border border-border rounded-xl bg-card p-4">
                  <GenericPriceCatalog
                    supplierId={supplier.id}
                    serviceSlug={slug}
                    serviceName={serviceNames[slug] || slug}
                  />
                </div>
              ))}

              {genericServices.length === 0 && !hasFasady && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  {t("supplierSettings.selectServiceFirst")}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
